import { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { Strategy, StrategyFamily } from '../types/Strategy';
import type { StrategyNode } from '../types/BaseNode';
import { deserializeNode } from '../utils/serializeNode';
import { UndoHandler } from '../utils/undoHandler';
import { cloneNode } from '../utils/handleDrop';

export interface UseStrategyCrudParams {
    strategies: Strategy[];
    setStrategies: React.Dispatch<React.SetStateAction<Strategy[]>>;
    setActiveStrategyId: React.Dispatch<React.SetStateAction<string | 'all' | null>>;
    setActiveTab: React.Dispatch<React.SetStateAction<'build' | 'results' | 'monitor' | 'alerts' | 'guide'>>;
    removeStrategyResult: (id: string) => void;
    initialState?: Partial<{ strategies: Strategy[] }>;
    getCurrentStrategyFamily: () => StrategyFamily;
}

export function useStrategyCrud({
    strategies,
    setStrategies,
    setActiveStrategyId,
    setActiveTab,
    removeStrategyResult,
    initialState,
    getCurrentStrategyFamily,
}: UseStrategyCrudParams) {
    const [undoHandlers, setUndoHandlers] = useState<Record<string, UndoHandler>>({});
    const strategiesRef = useRef<Strategy[]>(strategies);

    useEffect(() => {
        strategiesRef.current = strategies;
    }, [strategies]);

    // Initialize undo handlers for existing strategies
    useEffect(() => {
        const initialStrategies = initialState?.strategies;
        if (initialStrategies) {
            setUndoHandlers(prev => {
                const handlers = { ...prev };
                let changed = false;
                for (const s of initialStrategies) {
                    if (!handlers[s.id]) {
                        handlers[s.id] = new UndoHandler(20);
                        changed = true;
                    }
                }
                return changed ? handlers : prev;
            });
        }
    }, [initialState?.strategies]);

    const addNewStrategy = useCallback(() => {
        const newStrategy: Strategy = {
            id: uuidv4(),
            name: `Strategy ${strategiesRef.current.length + 1}`,
            strategyFamily: getCurrentStrategyFamily(),
            nodes: [],
            result: null,
            error: null,
            overlays: [],
        };
        setStrategies(prev => [...prev, newStrategy]);
        setUndoHandlers(prev => ({ ...prev, [newStrategy.id]: new UndoHandler(20) }));
        setActiveStrategyId(newStrategy.id);
    }, [getCurrentStrategyFamily, setStrategies, setActiveStrategyId]);

    const createStrategyFromNode = useCallback((node: StrategyNode) => {
        const nodeCopy = cloneNode(node);

        const newStrategy: Strategy = {
            id: uuidv4(),
            name: nodeCopy.title ?? 'New strategy from copy',
            strategyFamily: nodeCopy.type === 'MeepMeep' ? 'meepmeep' : getCurrentStrategyFamily(),
            nodes: [nodeCopy],
            result: null,
            error: null,
            overlays: [],
        };

        setStrategies(prev => [...prev, newStrategy]);
        setUndoHandlers(prev => ({ ...prev, [newStrategy.id]: new UndoHandler(20) }));
        setActiveStrategyId(newStrategy.id);
    }, [getCurrentStrategyFamily, setStrategies, setActiveStrategyId]);

    const loadFromMonitor = useCallback((strategyJson: string, name: string, monitorId?: number, conditionJson?: string) => {
        try {
            if (monitorId) {
                const existing = strategiesRef.current.find(s => s.monitorId === monitorId);
                if (existing) {
                    setActiveStrategyId(existing.id);
                    setActiveTab('build');
                    return;
                }
            }

            const parsed = JSON.parse(strategyJson);
            const rootNode = deserializeNode(parsed);

            let conditionRootId: string | undefined;
            if (conditionJson) {
                try {
                    const parsedCond = JSON.parse(conditionJson);
                    conditionRootId = parsedCond.id;
                } catch (e) {
                    console.error("Failed to parse condition json", e);
                }
            }

            const newStrategy: Strategy = {
                id: uuidv4(),
                name: name || 'Loaded Monitor Strategy',
                strategyFamily: rootNode.type === 'MeepMeep' ? 'meepmeep' : 'base',
                nodes: [rootNode],
                result: null,
                error: null,
                overlays: [],
                monitorId,
                conditionRootId,
            };

            setStrategies(prev => [...prev, newStrategy]);
            setUndoHandlers(prev => ({ ...prev, [newStrategy.id]: new UndoHandler(20) }));
            setActiveStrategyId(newStrategy.id);
            setActiveTab('build');
        } catch (e) {
            console.error('Failed to load strategy from monitor:', e);
            window.alert('Failed to load strategy: invalid context data.');
        }
    }, [setStrategies, setActiveStrategyId, setActiveTab]);

    const removeStrategy = useCallback((id: string) => {
        setStrategies(prev => prev.filter(s => s.id !== id));
        removeStrategyResult(id);
        setUndoHandlers(prev => {
            const copy = { ...prev };
            delete copy[id];
            return copy;
        });
        setActiveStrategyId(prevId => (prevId === id ? null : prevId));
    }, [setStrategies, setActiveStrategyId, removeStrategyResult]);

    const updateStrategy = useCallback((id: string, patch: Partial<Strategy>) => {
        setStrategies(prev =>
            prev.map(s => (s.id === id ? { ...s, ...patch } : s))
        );
    }, [setStrategies]);

    return {
        addNewStrategy,
        createStrategyFromNode,
        loadFromMonitor,
        removeStrategy,
        updateStrategy,
        undoHandlers,
    };
}
